import { useQueries } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { TimeRange, ConfidenceLabel } from '@/types/story'

export interface SectorIncidentsBucket {
  sector: string
  label: string
  count: number
}

export interface SectorIncidentsData {
  buckets: SectorIncidentsBucket[]
  selectedSector: string
  range: string
}

export interface ConfidenceRow {
  label: ConfidenceLabel
  count: number
}

export interface SectorConfidenceData {
  sectorLadder: ConfidenceRow[]
  corpusLadder: ConfidenceRow[]
  totalSector: number
  totalCorpus: number
}

// KAN-237 (P3): jurisdiction response shapes (parallel the sector ones but key
// on jurisdiction). The API returns canonical-normalised jurisdiction labels.
export interface JurisdictionIncidentsBucket {
  jurisdiction: string
  label: string
  count: number
}

export interface JurisdictionIncidentsData {
  buckets: JurisdictionIncidentsBucket[]
  selectedDimension: 'jurisdiction'
  selectedValue: string
  range: string
}

export interface JurisdictionConfidenceData {
  jurisdictionLadder: ConfidenceRow[]
  corpusLadder: ConfidenceRow[]
  totalJurisdiction: number
  totalCorpus: number
  selectedDimension: 'jurisdiction'
  selectedValue: string
}

// KAN-237 (P4) / KAN-233: country response shapes. The country dimension reads
// leak_site_claims.victim_country (ISO α2) — leak-site victim claims, NOT the
// article corpus — so buckets key on `countryIso2` (incl. the 'unknown' bucket
// for NULL victim_country). There is no confidence ladder for this dimension.
export interface CountryIncidentsBucket {
  countryIso2: string
  count: number
}

export interface CountryIncidentsData {
  buckets: CountryIncidentsBucket[]
  selectedDimension: 'country'
  selectedValue: string
  range: string
}

export interface CountryConfidenceData {
  selectedDimension: 'country'
  selectedValue: string
  /** Always false — leak-site country data has no confidence ladder. */
  confidenceApplicable: false
}

// KAN-237 (P6): vuln-class response shapes. The incident unit is the story
// (parity with sector/jurisdiction); buckets key on the taxonomy slug and always
// include the `unclassified` bucket. `totalStories` quantifies the coverage caveat.
export interface VulnClassIncidentsBucket {
  vulnClass: string
  label: string
  count: number
}

export interface VulnClassIncidentsData {
  buckets: VulnClassIncidentsBucket[]
  totalStories: number
  selectedDimension: 'vuln-class'
  selectedValue: string
  range: string
}

export interface VulnClassConfidenceData {
  vulnClassLadder: ConfidenceRow[]
  corpusLadder: ConfidenceRow[]
  totalVulnClass: number
  totalCorpus: number
  selectedDimension: 'vuln-class'
  selectedValue: string
}

/** The incidents endpoint returns the sector, jurisdiction, country OR vuln-class shape. */
export type CompareIncidentsData =
  | SectorIncidentsData
  | JurisdictionIncidentsData
  | CountryIncidentsData
  | VulnClassIncidentsData
/** The confidence endpoint returns the sector, jurisdiction, country OR vuln-class shape. */
export type CompareConfidenceData =
  | SectorConfidenceData
  | JurisdictionConfidenceData
  | CountryConfidenceData
  | VulnClassConfidenceData

/** True when an incidents payload is the jurisdiction shape. */
export function isJurisdictionIncidents(
  data: CompareIncidentsData | undefined,
): data is JurisdictionIncidentsData {
  return (
    data !== undefined &&
    (data as JurisdictionIncidentsData).selectedDimension === 'jurisdiction'
  )
}

/** True when a confidence payload is the jurisdiction shape. */
export function isJurisdictionConfidence(
  data: CompareConfidenceData | undefined,
): data is JurisdictionConfidenceData {
  return (
    data !== undefined &&
    (data as JurisdictionConfidenceData).selectedDimension === 'jurisdiction'
  )
}

/** True when an incidents payload is the country shape. */
export function isCountryIncidents(
  data: CompareIncidentsData | undefined,
): data is CountryIncidentsData {
  return (
    data !== undefined &&
    (data as CountryIncidentsData).selectedDimension === 'country'
  )
}

/** True when a confidence payload is the country shape. */
export function isCountryConfidence(
  data: CompareConfidenceData | undefined,
): data is CountryConfidenceData {
  return (
    data !== undefined &&
    (data as CountryConfidenceData).selectedDimension === 'country'
  )
}

/** True when an incidents payload is the vuln-class shape. */
export function isVulnClassIncidents(
  data: CompareIncidentsData | undefined,
): data is VulnClassIncidentsData {
  return (
    data !== undefined &&
    (data as VulnClassIncidentsData).selectedDimension === 'vuln-class'
  )
}

/** True when a confidence payload is the vuln-class shape. */
export function isVulnClassConfidence(
  data: CompareConfidenceData | undefined,
): data is VulnClassConfidenceData {
  return (
    data !== undefined &&
    (data as VulnClassConfidenceData).selectedDimension === 'vuln-class'
  )
}

export interface RegulatoryExposureBucket {
  sector: string
  label: string
  articleCount: number
  pctOfCorpus: number
}

export interface RegulatoryExposureData {
  buckets: RegulatoryExposureBucket[]
}

export interface CompareAnalyticsData {
  incidents: CompareIncidentsData | undefined
  confidence: CompareConfidenceData | undefined
  regulatory: RegulatoryExposureData | undefined
}

/**
 * KAN-237 (P3+): drive the Compare analytics with the generalised
 * `?dimension=&value=` axis. The incidents and confidence endpoints return
 * dimension-specific shapes; callers narrow with the type guards above.
 */
export function useCompareAnalytics(
  range: TimeRange,
  dimension: string,
  value: string,
) {
  const dimQuery = `dimension=${encodeURIComponent(dimension)}&value=${encodeURIComponent(value)}`
  const results = useQueries({
    queries: [
      {
        queryKey: [
          'analytics-compare',
          'sector-incidents',
          range,
          dimension,
          value,
        ],
        queryFn: () =>
          api.get<CompareIncidentsData>(
            `/analytics/compare/sector-incidents?range=${range}&${dimQuery}`,
          ),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: [
          'analytics-compare',
          'sector-confidence',
          range,
          dimension,
          value,
        ],
        queryFn: () =>
          api.get<CompareConfidenceData>(
            `/analytics/compare/sector-confidence?range=${range}&${dimQuery}`,
          ),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
      {
        queryKey: ['analytics-compare', 'regulatory-exposure', range],
        queryFn: () =>
          api.get<RegulatoryExposureData>(
            `/analytics/compare/regulatory-exposure?range=${range}`,
          ),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
      },
    ],
  })

  const [incidentsQ, confidenceQ, regulatoryQ] = results

  return {
    data: {
      incidents: incidentsQ.data,
      confidence: confidenceQ.data,
      regulatory: regulatoryQ.data,
    },
    status: {
      incidents: {
        isLoading: incidentsQ.isLoading,
        isError: incidentsQ.isError,
      },
      confidence: {
        isLoading: confidenceQ.isLoading,
        isError: confidenceQ.isError,
      },
      regulatory: {
        isLoading: regulatoryQ.isLoading,
        isError: regulatoryQ.isError,
      },
    },
    isLoading: results.some((q) => q.isLoading),
    isError: results.some((q) => q.isError),
    refetch: () => Promise.all(results.map((q) => q.refetch())),
  }
}
